import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { SoundCue } from '../../../audio/sound-cue';
import { AudioService } from '../../../services/audio.service';
import {
  AudioSettingsService,
  VolumeChannel,
} from '../../../services/audio-settings.service';

interface ChannelRow {
  key: VolumeChannel;
  label: string;
}

/**
 * Header mute button plus a volume popover (master / music / effects). M
 * toggles mute from anywhere except text fields.
 *
 * Phaser owns the keyboard inside a room, so while `inRoom` the controls
 * leave the tab order and give focus back after every pointer use — arrow
 * keys must keep steering the snake, never move a slider.
 */
@Component({
  selector: 'snk-sound-control',
  imports: [RouterLink],
  templateUrl: './sound-control.component.html',
  styleUrl: './sound-control.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'relative flex items-center',
    '(window:keydown)': 'onKeydown($event)',
    '(document:pointerdown)': 'onDocumentPointerDown($event)',
    '(pointerup)': 'releaseFocus()',
  },
})
export class SoundControlComponent {
  private readonly settingsService = inject(AudioSettingsService);
  private readonly audio = inject(AudioService);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  /** True on room routes, where Phaser needs keyboard focus. */
  readonly inRoom = input(false);

  readonly open = signal(false);
  readonly muted = this.settingsService.muted;

  readonly channels: readonly ChannelRow[] = [
    { key: 'master', label: 'MASTER' },
    { key: 'music', label: 'MUSIC' },
    { key: 'sfx', label: 'EFFECTS' },
  ];

  /** Slider values as whole percentages. */
  readonly percents = computed<Record<VolumeChannel, number>>(() => {
    const s = this.settingsService.settings();
    return {
      master: Math.round(s.master * 100),
      music: Math.round(s.music * 100),
      sfx: Math.round(s.sfx * 100),
    };
  });

  /** `null` keeps the default tab order outside a room. */
  readonly tabIndex = computed(() => (this.inRoom() ? -1 : null));

  toggleMute(): void {
    this.settingsService.toggleMute();
  }

  togglePanel(): void {
    this.open.update((v) => !v);
  }

  onVolumeInput(channel: VolumeChannel, event: Event): void {
    const value = Number((event.target as HTMLInputElement).value);
    this.settingsService.setVolume(channel, value / 100);
  }

  /** Slider released: preview the effects level, then hand focus back. */
  onVolumeCommit(channel: VolumeChannel): void {
    if (channel === 'sfx') this.audio.play(SoundCue.UI_CLICK);
    this.releaseFocus();
  }

  onMuteChange(event: Event): void {
    this.settingsService.setMuted((event.target as HTMLInputElement).checked);
    this.releaseFocus();
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape' && this.open()) {
      this.open.set(false);
      return;
    }
    if (event.key !== 'm' && event.key !== 'M') return;
    if (event.repeat || event.ctrlKey || event.metaKey || event.altKey) return;
    if (isTextEntry(event.target)) return;
    this.toggleMute();
  }

  onDocumentPointerDown(event: PointerEvent): void {
    if (!this.open()) return;
    if (!this.host.nativeElement.contains(event.target as Node)) {
      this.open.set(false);
    }
  }

  /** Inside a room, never keep focus on a DOM control after using it. */
  releaseFocus(): void {
    if (!this.inRoom()) return;
    const active = document.activeElement;
    if (
      active instanceof HTMLElement &&
      this.host.nativeElement.contains(active)
    ) {
      active.blur();
    }
  }
}

function isTextEntry(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  if (target instanceof HTMLTextAreaElement) return true;
  if (target instanceof HTMLInputElement) {
    return !['range', 'checkbox', 'radio', 'button', 'submit'].includes(
      target.type
    );
  }
  return target instanceof HTMLSelectElement;
}
